import { Clock, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import type { AnalyticsMetrics } from '../../api';

interface MetricsCardsProps {
  metrics: AnalyticsMetrics;
  totalComplaints: number;
  isLoading?: boolean;
}

/**
 * Format milliseconds to human-readable time string
 */
function formatDuration(ms: number | null): { value: string; unit: string } {
  if (ms === null || ms === 0) return { value: '0', unit: 'hrs' };

  const hours = ms / (1000 * 60 * 60);
  if (hours < 1) {
    const minutes = Math.round(ms / (1000 * 60));
    return { value: String(minutes), unit: 'min' };
  }
  if (hours < 24) {
    return { value: String(Math.round(hours)), unit: 'hrs' };
  }

  const days = hours / 24;
  if (days < 7) {
    return { value: String(Math.round(days * 10) / 10), unit: 'days' };
  }

  return { value: String(Math.round(days)), unit: 'days' };
}

/**
 * Circular progress ring component for rate metrics
 */
function ProgressRing({ percentage, color, size = 56 }: { percentage: number; color: string; size?: number }) {
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const colorMap: Record<string, string> = {
    yellow: '#eab308',
    green: '#22c55e',
    blue: '#DE1D3B',
    purple: '#a855f7',
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colorMap[color] || color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
      </svg>
    </div>
  );
}

export function MetricsCards({ metrics, totalComplaints, isLoading }: MetricsCardsProps) {
  const duration = formatDuration(metrics.avgResolutionTime);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-4 border border-gray-100 animate-pulse">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full mb-3" />
              <div className="h-7 bg-gray-200 rounded w-12 mb-1" />
              <div className="h-3 bg-gray-100 rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Total Complaints */}
      <div data-testid="analytics-card-total" className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <FileText className="w-5 h-5 text-gray-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalComplaints}</p>
          <p className="text-xs text-gray-500 font-medium">Complaints</p>
        </div>
      </div>

      {/* Avg Resolution Time */}
      <div data-testid="analytics-card-resolution-time" className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mb-3">
            <Clock className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {duration.value}
            <span className="text-sm font-medium text-gray-500 ml-1">{duration.unit}</span>
          </p>
          <p className="text-xs text-gray-500 font-medium">Avg Resolution</p>
        </div>
      </div>

      {/* Acknowledgment Rate */}
      <div data-testid="analytics-card-acknowledgment-rate" className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-yellow-200 hover:shadow-md transition-all duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-3">
            <ProgressRing percentage={metrics.acknowledgmentRate} color="yellow" size={48} />
            <div className="absolute inset-0 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.acknowledgmentRate}%</p>
          <p className="text-xs text-gray-500 font-medium">Acknowledged</p>
        </div>
      </div>

      {/* Resolution Rate */}
      <div data-testid="analytics-card-resolution-rate" className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-green-200 hover:shadow-md transition-all duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-3">
            <ProgressRing percentage={metrics.resolutionRate} color="green" size={48} />
            <div className="absolute inset-0 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.resolutionRate}%</p>
          <p className="text-xs text-gray-500 font-medium">Resolved</p>
        </div>
      </div>
    </div>
  );
}
